/**
 * AudioEngine — the hardware half of WebSession.
 *
 * Capture: getUserMedia (echo cancellation + noise suppression + AGC) into
 * an AudioWorkletNode at 16 kHz; Float32 -> Int16LE -> base64 in ~128 ms
 * chunks (2048 samples). The worklet module is inlined as a Blob URL so the
 * UMD build works from a plain script tag with no separate asset.
 *
 * Playback: decoded PCM queued as AudioBufferSourceNodes at 16 kHz;
 * `clear()` stops the current source and drops the queue (barge-in).
 */

import type { AudioEngineLike } from './session';

const SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = 2048; // ~128 ms at 16 kHz

// Runs on the audio thread: batch input samples into fixed-size chunks and
// post them to the main thread. Kept dependency-free and stringified.
const WORKLET_SOURCE = `
class OmniCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Float32Array(${CHUNK_SAMPLES});
    this.offset = 0;
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;
    let read = 0;
    while (read < channel.length) {
      const take = Math.min(channel.length - read, this.buffer.length - this.offset);
      this.buffer.set(channel.subarray(read, read + take), this.offset);
      this.offset += take;
      read += take;
      if (this.offset === this.buffer.length) {
        this.port.postMessage(this.buffer.slice(0));
        this.offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('omni-capture', OmniCaptureProcessor);
`;

function floatToBase64Pcm16(samples: Float32Array): string {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function base64ToFloat(b64: string) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / 0x8000;
  return out;
}

export class AudioEngine implements AudioEngineLike {
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private playhead = 0;
  private playing: AudioBufferSourceNode[] = [];

  async start(onChunk: (base64Pcm: string) => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: SAMPLE_RATE,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.context = new AudioContext({ sampleRate: SAMPLE_RATE });
    // Safari (and Chrome autoplay policy) suspends a context created after the
    // user gesture was "spent" by an await (e.g. the server round-trip that
    // produced the ws_url). Without resume() there is silence both ways.
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }

    const blobUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: 'application/javascript' }),
    );
    try {
      await this.context.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }

    this.workletNode = new AudioWorkletNode(this.context, 'omni-capture');
    this.workletNode.port.onmessage = (ev: MessageEvent<Float32Array>) => {
      onChunk(floatToBase64Pcm16(ev.data));
    };
    const source = this.context.createMediaStreamSource(this.stream);
    source.connect(this.workletNode);
    // The worklet only posts messages; nothing is connected to the output,
    // so the mic is never looped back to the speakers.
  }

  enqueue(base64Pcm: string): void {
    if (!this.context) return;
    const samples = base64ToFloat(base64Pcm);
    if (samples.length === 0) return;
    const buffer = this.context.createBuffer(1, samples.length, SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    const at = Math.max(this.context.currentTime, this.playhead);
    source.start(at);
    this.playhead = at + buffer.duration;
    this.playing.push(source);
    source.onended = () => {
      this.playing = this.playing.filter((s) => s !== source);
    };
  }

  clear(): void {
    for (const source of this.playing) {
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
    }
    this.playing = [];
    this.playhead = 0;
  }

  setMuted(muted: boolean): void {
    // Toggling track.enabled keeps the stream warm: unmute needs no
    // re-permission and no re-negotiation.
    for (const track of this.stream?.getAudioTracks() ?? []) {
      track.enabled = !muted;
    }
  }

  stop(): void {
    this.clear();
    this.workletNode?.disconnect();
    this.workletNode = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    void this.context?.close();
    this.context = null;
  }
}
